import { isPast } from "date-fns";
import {
  CalendarOff,
  EyeIcon,
  MoreVerticalIcon,
  PencilIcon,
} from "lucide-react";
import { useState } from "react";
import { Link, matchPath } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCancelEvent } from "@/features/events/api/cancel-event";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicEvent } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

type EventActionsProps = {
  event: PublicEvent;
};

export function EventActions({ event }: EventActionsProps) {
  const { organisation } = useOrganisation();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    mutateAsync: cancelEvent,
    isPending,
    isError,
    error,
  } = useCancelEvent({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setIsDialogOpen(false);
    },
  });
  const isMyEventsView = matchPath("/events/my", location.pathname) !== null;
  const isSingleEventView = !isMyEventsView;
  const handleCancel = async () => {
    // This should never happen
    if (!organisation?.id) {
      return;
    }

    try {
      await cancelEvent({ eventId: event.id, orgId: organisation.id });
    } catch (error) {
      console.error(error);
    }
  };

  const isEventPast = isPast(event.session.end);

  if (isEventPast && isSingleEventView) {
    return null;
  }

  if (isEventPast) {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon className="size-4" aria-hidden={true} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Link
              to={`/events/${event.id}`}
              className="flex w-full cursor-pointer items-center gap-2"
            >
              View
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon className="size-4" aria-hidden={true} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isSingleEventView && (
            <DropdownMenuItem>
              <Link
                to={`/events/${event.id}`}
                className="flex w-full cursor-pointer items-center gap-2"
              >
                <EyeIcon className="size-4" aria-hidden={true} />
                View
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem>
            <Link
              to={`/events/${event.id}/edit`}
              className="flex w-full cursor-pointer items-center gap-2"
            >
              <PencilIcon className="size-4" aria-hidden={true} />
              Edit
            </Link>
          </DropdownMenuItem>
          <DialogTrigger asChild>
            <DropdownMenuItem className="cursor-pointer text-destructive">
              <CalendarOff className="size-4" aria-hidden={true} />
              Cancel Event
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you would like to cancel this event?
          </DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        {isError && <ErrorMessage error={error} />}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            type="submit"
            onClick={handleCancel}
            isLoading={isPending}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
